// src/components/layout/Sidebar.tsx
import React from 'react';
import {
  Drawer,
  List,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  Tooltip,
  Box,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  Dashboard as DashboardIcon,
  Apps as AppsIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  Analytics as AnalyticsIcon,
  Help as HelpIcon,
} from '@mui/icons-material';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const drawerWidth = 240;

interface SidebarProps {
  open: boolean;
  handleDrawerClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ open, handleDrawerClose }) => {
  const theme = useTheme();
  const location = useLocation();
  const { user } = useAuth();

  // Menüelemek adminoknak és normál felhasználóknak
  const menuItems = [
    {
      text: 'Irányítópult',
      icon: <DashboardIcon />,
      path: '/',
      roles: ['admin', 'user', 'guest'],
    },
    {
      text: 'Alkalmazások',
      icon: <AppsIcon />,
      path: '/apps',
      roles: ['admin', 'user'],
    },
  ];

  // Adminisztrátor menüelemek
  const adminMenuItems = [
    {
      text: 'Felhasználók',
      icon: <PeopleIcon />,
      path: '/users',
      roles: ['admin'],
    },
    {
      text: 'Rendszerfelügyelet',
      icon: <AnalyticsIcon />,
      path: '/system',
      roles: ['admin'],
    },
    {
      text: 'Beállítások',
      icon: <SettingsIcon />,
      path: '/settings',
      roles: ['admin'],
    },
  ];

  // Egyéb menüelemek
  const otherMenuItems = [
    {
      text: 'Súgó',
      icon: <HelpIcon />,
      path: '/help',
      roles: ['admin', 'user', 'guest'],
    },
  ];

  // Ellenőrizzük, hogy a felhasználónak van-e jogosultsága látni a menüpontot
  const hasPermission = (requiredRoles: string[]) => {
    if (!user || !user.role) return false;
    return requiredRoles.includes(user.role);
  };

  // Aktív-e a menüpont
  const isActive = (path: string) => location.pathname === path;

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: open ? drawerWidth : theme.spacing(7),
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? drawerWidth : theme.spacing(7),
          boxSizing: 'border-box',
          transition: theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          overflowX: 'hidden',
          backgroundColor: theme.palette.background.paper,
          borderRight: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          p: 1,
        }}
      >
        <IconButton onClick={handleDrawerClose}>
          <ChevronLeftIcon />
        </IconButton>
      </Box>
      
      <Divider />
      
      <List>
        {menuItems.map(
          (item) =>
            hasPermission(item.roles) && (
              <ListItem
                key={item.text}
                disablePadding
                component={Link}
                to={item.path}
                sx={{ display: 'block' }}
              >
                <Tooltip title={open ? '' : item.text} placement="right">
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                      backgroundColor: isActive(item.path)
                        ? theme.palette.action.selected
                        : 'transparent',
                      borderLeft: isActive(item.path)
                        ? `4px solid ${theme.palette.primary.main}`
                        : '4px solid transparent',
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                        color: isActive(item.path)
                          ? theme.palette.primary.main
                          : theme.palette.text.secondary,
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      sx={{
                        opacity: open ? 1 : 0,
                        color: isActive(item.path)
                          ? theme.palette.primary.main
                          : theme.palette.text.primary,
                      }}
                    />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            )
        )}
      </List>
      
      <Divider />
      
      {/* Admin menüpontok, csak ha admin a felhasználó */}
      {user?.role === 'admin' && (
        <>
          <List>
            {adminMenuItems.map(
              (item) =>
                hasPermission(item.roles) && (
                  <ListItem
                    key={item.text}
                    disablePadding
                    component={Link}
                    to={item.path}
                    sx={{ display: 'block' }}
                  >
                    <Tooltip title={open ? '' : item.text} placement="right">
                      <ListItemButton
                        sx={{
                          minHeight: 48,
                          justifyContent: open ? 'initial' : 'center',
                          px: 2.5,
                          backgroundColor: isActive(item.path)
                            ? theme.palette.action.selected
                            : 'transparent',
                          borderLeft: isActive(item.path)
                            ? `4px solid ${theme.palette.primary.main}`
                            : '4px solid transparent',
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: open ? 3 : 'auto',
                            justifyContent: 'center',
                            color: isActive(item.path)
                              ? theme.palette.primary.main
                              : theme.palette.text.secondary,
                          }}
                        >
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          sx={{
                            opacity: open ? 1 : 0,
                            color: isActive(item.path)
                              ? theme.palette.primary.main
                              : theme.palette.text.primary,
                          }}
                        />
                      </ListItemButton>
                    </Tooltip>
                  </ListItem>
                )
            )}
          </List>
          
          <Divider />
        </>
      )}
      
      <List>
        {otherMenuItems.map(
          (item) =>
            hasPermission(item.roles) && (
              <ListItem
                key={item.text}
                disablePadding
                component={Link}
                to={item.path}
                sx={{ display: 'block' }}
              >
                <Tooltip title={open ? '' : item.text} placement="right">
                  <ListItemButton
                    sx={{
                      minHeight: 48,
                      justifyContent: open ? 'initial' : 'center',
                      px: 2.5,
                      backgroundColor: isActive(item.path)
                        ? theme.palette.action.selected
                        : 'transparent',
                      borderLeft: isActive(item.path)
                        ? `4px solid ${theme.palette.primary.main}`
                        : '4px solid transparent',
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 0,
                        mr: open ? 3 : 'auto',
                        justifyContent: 'center',
                        color: isActive(item.path)
                          ? theme.palette.primary.main
                          : theme.palette.text.secondary,
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.text}
                      sx={{
                        opacity: open ? 1 : 0,
                        color: isActive(item.path)
                          ? theme.palette.primary.main
                          : theme.palette.text.primary,
                      }}
                    />
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            )
        )}
      </List>
    </Drawer>
  );
};

export default Sidebar;